// frontend/src/components/UserManagementTable.tsx
import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, Button, CustomSelect, LoadingButton } from '@redpanda/ui';
import { Role, UserStatus, User } from '@redpanda/types';
import { getApprovedUsers, getPendingUsers, updateUserRole, deleteUser, approveUser, rejectUser } from '../services/api';

const UserManagementTable: React.FC = () => {
  const { t } = useTranslation();
  const [approvedUsers, setApprovedUsers] = useState<User[]>(() => {
    try {
      const cached = localStorage.getItem('adminPanel_approvedUsers');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [pendingUsers, setPendingUsers] = useState<User[]>(() => {
    try {
      const cached = localStorage.getItem('adminPanel_pendingUsers');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [error, setError] = useState<string | null>(null);
  const userContext = useContext(UserContext);
  const currentUserId = userContext?.user?.id;
  const currentUserRole = userContext?.user?.role;

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [userToDeleteId, setUserToDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [approvingUsers, setApprovingUsers] = useState<Set<string>>(new Set());
  const [rejectingUsers, setRejectingUsers] = useState<Set<string>>(new Set());

  const fetchUsers = async () => {
    setError(null);

    try {
      const [approvedResponse, pendingResponse] = await Promise.all([
        getApprovedUsers(),
        getPendingUsers(),
      ]);

      setApprovedUsers(approvedResponse.data.users);
      setPendingUsers(pendingResponse.data);

      // Cache the data
      localStorage.setItem('adminPanel_approvedUsers', JSON.stringify(approvedResponse.data.users));
      localStorage.setItem('adminPanel_pendingUsers', JSON.stringify(pendingResponse.data));

    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'An unknown error occurred');
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleRoleChange = async (id: string, newRole: Role) => {
    try {
      await updateUserRole(id, newRole);
      const updatedUsers = approvedUsers.map(user => (user.id === id ? { ...user, role: newRole } : user));
      setApprovedUsers(updatedUsers);
      localStorage.setItem('adminPanel_approvedUsers', JSON.stringify(updatedUsers));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user role');
    }
  };

  const handleDeleteUser = (id: string) => {
    if (id === currentUserId) {
      setError(t('common.messages.cannotDeleteSelf'));
      return;
    }
    setUserToDeleteId(id);
    setShowConfirmModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDeleteId) return;
    setIsDeleting(true);
    try {
      await deleteUser(userToDeleteId);
      const filteredUsers = approvedUsers.filter(user => user.id !== userToDeleteId);
      setApprovedUsers(filteredUsers);
      localStorage.setItem('adminPanel_approvedUsers', JSON.stringify(filteredUsers));
      setShowConfirmModal(false);
      setUserToDeleteId(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete user');
      setShowConfirmModal(false);
      setUserToDeleteId(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleApproveUser = async (id: string) => {
    const roleToAssign = Role.USER; // Always assign USER role by default
    setApprovingUsers(prev => new Set(prev).add(id));
    try {
      // 審核通過的用戶給予 ALL 權限，可存取所有業務系統
      await approveUser(id, roleToAssign, {
        allowedSystems: ['ALL']
      });

      const approvedUser = pendingUsers.find(user => user.id === id);
      if (approvedUser) {
        const filteredPending = pendingUsers.filter(user => user.id !== id);
        const updatedApproved = [...approvedUsers, {
          ...approvedUser,
          status: UserStatus.APPROVED,
          role: roleToAssign,
          allowedSystems: ['ALL']
        }];
        setPendingUsers(filteredPending);
        setApprovedUsers(updatedApproved);
        localStorage.setItem('adminPanel_pendingUsers', JSON.stringify(filteredPending));
        localStorage.setItem('adminPanel_approvedUsers', JSON.stringify(updatedApproved));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve user');
    } finally {
      setApprovingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleRejectUser = async (id: string) => {
    setRejectingUsers(prev => new Set(prev).add(id));
    try {
      await rejectUser(id);
      const filteredPending = pendingUsers.filter(user => user.id !== id);
      setPendingUsers(filteredPending);
      localStorage.setItem('adminPanel_pendingUsers', JSON.stringify(filteredPending));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reject user');
    } finally {
      setRejectingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };


  if (error) {
    return <div className="p-8 text-center text-red-500">{error}</div>;
  }

  return (
    <>
      <div className="space-y-8">
        {/* Approved Users Table */}
        <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white p-6 border-b border-gray-200 dark:border-slate-700">{t('userManagement.approvedUsers')}</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/5">{t('common.table.columns.email')}</TableHead>
                <TableHead className="w-1/5">{t('common.table.columns.name')}</TableHead>
                <TableHead className="w-1/5">{t('common.table.columns.role')}</TableHead>
                <TableHead className="w-1/5">{t('common.table.columns.createdAt')}</TableHead>
                <TableHead className="w-1/5">{t('common.table.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {approvedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    {t('userManagement.noApprovedUsers')}
                  </TableCell>
                </TableRow>
              ) : (
                approvedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="w-1/5 font-medium truncate">{user.email}</TableCell>
                    <TableCell className="w-1/5 truncate">{user.name || '-'}</TableCell>
                    <TableCell className="w-1/5">
                      {currentUserRole === Role.SUPER_ADMIN && user.role !== Role.SUPER_ADMIN ? (
                        <CustomSelect
                          value={user.role}
                          onChange={(value: string) => handleRoleChange(user.id, value as Role)}
                          options={[
                            { value: Role.USER, label: t('common.roles.user') },
                            { value: Role.ADMIN, label: t('common.roles.admin') }
                          ]}
                        />
                      ) : (
                        <span className={`font-semibold ${user.role === Role.SUPER_ADMIN ? 'text-yellow-600' : 'text-gray-900 dark:text-white'}`}>
                          {t(`common.roles.${user.role.toLowerCase()}`)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="w-1/5 truncate">{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell className="w-1/5">
                      {currentUserRole === Role.SUPER_ADMIN && user.role !== Role.SUPER_ADMIN && user.id !== currentUserId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-200"
                        >
                          {t('common.actions.delete')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pending Users Section */}
        {pendingUsers.length > 0 && (
          <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white p-6 border-b border-gray-200 dark:border-slate-700">{t('userManagement.pendingUsers')}</h2>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/4">{t('common.table.columns.email')}</TableHead>
                  <TableHead className="w-1/4">{t('common.table.columns.name')}</TableHead>
                  <TableHead className="w-1/4">{t('common.table.columns.createdAt')}</TableHead>
                  <TableHead className="w-1/4">{t('common.table.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="w-1/4 font-medium truncate">{user.email}</TableCell>
                    <TableCell className="w-1/4 truncate">{user.name || '-'}</TableCell>
                    <TableCell className="w-1/4 truncate">{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell className="w-1/4">
                      <div className="flex items-center space-x-2">
                        <LoadingButton
                          type="button"
                          loading={approvingUsers.has(user.id)}
                          disabled={approvingUsers.has(user.id) || rejectingUsers.has(user.id)}
                          onClick={() => handleApproveUser(user.id)}
                          className="text-green-600 hover:text-white hover:bg-green-500 dark:text-green-400 dark:hover:text-white dark:hover:bg-green-600 bg-transparent border border-green-200 hover:border-green-500 dark:border-green-700 dark:hover:border-green-600 px-3 py-1.5 text-sm rounded-md transition-all duration-200 ease-in-out transform hover:scale-105"
                        >
                          {approvingUsers.has(user.id) ? t('userManagement.approving') : t('userManagement.approve')}
                        </LoadingButton>
                        <LoadingButton
                          type="button"
                          loading={rejectingUsers.has(user.id)}
                          disabled={approvingUsers.has(user.id) || rejectingUsers.has(user.id)}
                          onClick={() => handleRejectUser(user.id)}
                          className="text-red-600 hover:text-white hover:bg-red-500 dark:text-red-400 dark:hover:text-white dark:hover:bg-red-600 bg-transparent border border-red-200 hover:border-red-500 dark:border-red-700 dark:hover:border-red-600 px-3 py-1.5 text-sm rounded-md transition-all duration-200 ease-in-out transform hover:scale-105"
                        >
                          {rejectingUsers.has(user.id) ? t('userManagement.rejecting') : t('userManagement.reject')}
                        </LoadingButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          if (!isDeleting) {
            setShowConfirmModal(false);
            setUserToDeleteId(null);
          }
        }}
        onConfirm={confirmDeleteUser}
        title={t('userManagement.confirmDelete.title')}
        message={t('userManagement.confirmDelete.message')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('userManagement.confirmDelete.deleting')}
      />
    </>
  );
};

export default UserManagementTable;